/**
 * redux/slices/auth.ts — session state slice.
 *
 * Holds the access token, refresh token, per-session secretKey, and the
 * current user profile. Login is a thunk that calls the API and dispatches
 * `loginSuccess` on success or sets `error` on failure.
 *
 * Refresh token is rehydrated from localStorage on store boot via the
 * `rehydrate` action — see index.tsx. Access token + secretKey live in
 * memory only and reset on every page reload (the axios interceptor will
 * trigger a refresh on the first 401, which exchanges the saved refresh
 * token for a fresh trio).
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';

import * as apiClient from '../../services/api';
import type { LoginResponse, User } from '../../type/api';
import { clearRefreshToken, loadRefreshToken, saveRefreshToken } from '../../helpers/auth';
import type { RootState } from '../store';

export interface AuthState {
	user: User | null;
	accessToken: string | null;
	refreshToken: string | null;
	secretKey: string | null;
	status: 'idle' | 'loading' | 'authed' | 'error';
	error: string | null;
}

const INITIAL: AuthState = {
	user: null,
	accessToken: null,
	refreshToken: loadRefreshToken(),
	secretKey: null,
	status: 'idle',
	error: null,
};

// ── Thunks ──────────────────────────────────────────────────────────────

export const loginThunk = createAsyncThunk<
	LoginResponse,
	{ username: string; password: string },
	{ rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
	try {
		const envelope = await apiClient.login(credentials.username, credentials.password);
		if (envelope.StatusCode === '01' && typeof envelope.Response === 'object' && envelope.Response !== null) {
			return envelope.Response as LoginResponse;
		}
		const msg =
			typeof envelope.Response === 'string' ? envelope.Response : 'Invalid username or password';
		return rejectWithValue(msg);
	} catch (err: unknown) {
		const fallback = err instanceof Error ? err.message : 'Network error';
		return rejectWithValue(fallback);
	}
});

export const logoutThunk = createAsyncThunk('auth/logout', async () => {
	await apiClient.logout();
});

// ── Slice ───────────────────────────────────────────────────────────────

const authSlice = createSlice({
	name: 'auth',
	initialState: INITIAL,
	reducers: {
		/** Manually rotate the trio — used by the axios refresh interceptor. */
		tokensRotated(
			state,
			action: PayloadAction<{ accessToken: string; refreshToken: string; secretKey: string }>,
		) {
			state.accessToken = action.payload.accessToken;
			state.refreshToken = action.payload.refreshToken;
			state.secretKey = action.payload.secretKey;
			saveRefreshToken(action.payload.refreshToken);
		},
		/** Reset the form's loading/error after the user dismisses an error. */
		clearLoginError(state) {
			state.error = null;
			if (state.status === 'error') state.status = 'idle';
		},
		/** Hard reset — used by the 401 refresh-fail path in axios. */
		sessionCleared(state) {
			state.user = null;
			state.accessToken = null;
			state.refreshToken = null;
			state.secretKey = null;
			state.status = 'idle';
			state.error = null;
			clearRefreshToken();
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loginThunk.pending, (state) => {
				state.status = 'loading';
				state.error = null;
			})
			.addCase(loginThunk.fulfilled, (state, action) => {
				state.user = action.payload.user;
				state.accessToken = action.payload.accessToken;
				state.refreshToken = action.payload.refreshToken;
				state.secretKey = action.payload.secratekey;
				state.status = 'authed';
				state.error = null;
				saveRefreshToken(action.payload.refreshToken);
			})
			.addCase(loginThunk.rejected, (state, action) => {
				state.status = 'error';
				state.error = action.payload || 'Login failed';
			})
			.addCase(logoutThunk.fulfilled, (state) => {
				state.user = null;
				state.accessToken = null;
				state.refreshToken = null;
				state.secretKey = null;
				state.status = 'idle';
				state.error = null;
				clearRefreshToken();
			});
	},
});

export const { tokensRotated, clearLoginError, sessionCleared } = authSlice.actions;
export default authSlice.reducer;

// ── Selectors ───────────────────────────────────────────────────────────

export const selectAuth = (s: RootState): AuthState => s.auth;
export const selectUser = (s: RootState): User | null => s.auth.user;
export const selectIsAuthed = (s: RootState): boolean => Boolean(s.auth.accessToken && s.auth.user);
export const selectAuthStatus = (s: RootState): AuthState['status'] => s.auth.status;
export const selectAuthError = (s: RootState): string | null => s.auth.error;
